/**
 * The projected pictures, in the numbers every screen agrees on (print/MARKETING/film/SCREENS-PLAN.md).
 *
 * The room has three: the main projector on the bar wall, which carries the TV picture and the
 * surround around it; and a projector looking straight down at a strip along the middle of the
 * long table. The table strip is measured in millimetres, so a guest's place on it is where their
 * plate actually is.
 */
import { COURSES, SERVES } from './look.js';
import type { Chapter } from './types.js';

/** The strip down the middle of the table. */
export const TABLE = {
  lengthMm: 3450,
  widthMm: 800,
  /** Five rows of two along the sides, seat 1 nearest the screen: seats 1 to 10. */
  rows: 5,
  /**
   * Seats 11 and 12 sit at the round far end, one each side of the middle, turned 45° to the table
   * (Marek, 2026-09-25: ten along the sides and two at the round end).
   */
  headSeats: 2,
  /** How far in from the strip's edge a guest's place is drawn. */
  placeInsetMm: 95,
} as const;

/** Every seat the table can lay: ten along the sides and two at the round far end. */
export const TABLE_SEATS = TABLE.rows * 2 + TABLE.headSeats;

/** A direction or a point on the strip, in millimetres: x from the far end, y from the odd seats' edge. */
export interface StripPoint {
  x: number;
  y: number;
}

export interface TablePlace {
  seat: number;
  /**
   * Which of the twelve places the seat is at tonight: 1–10 along the sides (odd on the odd side, 1
   * and 2 the row nearest the TV), 11 and 12 at the round far end. With every place open, the seat's
   * own number (`seatOrder`).
   */
  place: number;
  /** 1 nearest the TV, 5 the last along the sides, 6 the two at the round far end. */
  row: number;
  /** The place's side: odd places on one side, even on the other; at the far end, 11 is on the odd side of the middle. */
  side: 'odd' | 'even';
  /** One of the two at the round far end, sitting at 45° to the table. */
  head: boolean;
  /** The place itself, where the plate is: millimetres from the far end of the strip towards the TV end. */
  x: number;
  /** Millimetres across the strip from the odd-seat edge. */
  y: number;
  /** Where the guest meets the strip. */
  edge: StripPoint;
  /** The way the guest looks across the table: a unit vector from `edge` into the strip. */
  toward: StripPoint;
  /** How wide the guest's own stretch of the table is, in millimetres, for the words at their place. */
  roomMm: number;
}

/** A strip's size, as measured on the real table (the mapping window); TABLE until then. */
export interface StripSize {
  lengthMm: number;
  widthMm: number;
  /**
   * The places laid tonight, in the order the seats take them (Marek, 2026-09-29: a picture of the
   * table in the mapping window, a tap opens or closes each place): place numbers as
   * `TablePlace.place`, seat n at the n-th. Laid by `layPlaces`, lengthened for a larger crew by
   * `placesForCrew`. The guests keep seats 1…n; everything the table draws at a seat, and the
   * camera's places, follow. Unset: all twelve, seat n at place n.
   */
  openPlaces?: readonly number[] | null;
  /**
   * The older way of saying it, still read when `openPlaces` is not given: rows along the sides left
   * empty at the bar end (Marek, 2026-09-27), and the odd side's own count when it differs.
   */
  freeRows?: number;
  freeRowsOdd?: number | null | undefined;
  /**
   * Where along the strip each place along the sides really is, in millimetres from the far end: the
   * plate laid there, as the table projector's linked plates say (Marek, 2026-09-29: "according to the
   * positions of the plates you can also align the name tags"). A place with none keeps the even rows.
   */
  placeAlong?: Readonly<Partial<Record<number, number>>> | null;
  /**
   * Each place's words moved by hand onto its plate (Marek, 2026-09-30: "shift the name tags and
   * adjust their position precisely to align them with plates"), in millimetres: `along` toward the
   * TV end, `across` in from the guest's edge toward the middle, the way they look. On top of
   * `placeAlong`; everything written at the place moves with it (its tag, what is served, PLAY NOW,
   * the scores).
   */
  placeShift?: Readonly<Partial<Record<number, PlaceShift>>> | null;
}

/**
 * A place's words moved by hand (`StripSize.placeShift`), in millimetres: `along` toward the TV end
 * (away from it when less than 0), `across` in toward the middle of the table the way the guest looks
 * (out toward the guest when less than 0; at the round end, on the slant their words face).
 */
export interface PlaceShift {
  along: number;
  across: number;
}

/** How far a place's words may be moved by hand, either way: enough for any plate, never off the table. */
export const PLACE_SHIFT_MAX_MM = 250;

/**
 * How far a whole place — its plate, its coaster and its words together — may be moved for one
 * evening, either way (Marek, 2026-10-06: the phone's table map, "adjust the seat exact position").
 */
export const PLACE_MOVE_MAX_MM = 250;

/** How much the gap between two neighbouring rows along the sides may grow or shrink for one evening. */
export const ROW_SPACING_MAX_MM = 80;

/**
 * How far a place's words can end up from where the rows put them: moved onto the plate by hand,
 * the whole place moved for the evening, and the rows spread or drawn in.
 */
export const PLACE_REACH_MM = PLACE_SHIFT_MAX_MM + PLACE_MOVE_MAX_MM + 2 * ROW_SPACING_MAX_MM;

/** A number of millimetres, whole, within `max` either way; anything else is none. */
const mmWithin = (n: unknown, max: number) => (typeof n === 'number' && Number.isFinite(n) ? Math.round(Math.max(-max, Math.min(max, n))) : 0);

/** The move of a place's words, checked; none when there is none. */
export function placeShiftOf(strip: Pick<StripSize, 'placeShift'>, place: number): PlaceShift {
  const s = strip.placeShift?.[place];
  const mm = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) ? Math.max(-PLACE_REACH_MM, Math.min(PLACE_REACH_MM, n)) : 0);
  return { along: mm(s?.along), across: mm(s?.across) };
}

/**
 * How far the rows' spacing moves a place along the table (Marek, 2026-10-06: "adjust … the spacing
 * between them"): every gap between neighbouring rows along the sides grows by `spacing` millimetres,
 * about the middle row, so the row nearest the TV goes toward it by two gaps' worth and the last row
 * toward the round end. The round end's two stay where they are.
 */
export function spacingAlong(place: number, spacing: number): number {
  const row = Math.ceil(place / 2);
  if (!Number.isInteger(place) || row < 1 || row > TABLE.rows) return 0;
  return ((TABLE.rows + 1) / 2 - row) * mmWithin(spacing, ROW_SPACING_MAX_MM);
}

/** An evening's moves of whole places: each place by hand, and the rows' spacing. */
export interface PlaceMoves {
  moves?: Readonly<Partial<Record<number, PlaceShift>>> | null;
  spacing?: number | null;
}

/**
 * Where a whole place is moved for the evening, in millimetres, the way `PlaceShift` says it: its own
 * move and the rows' spacing together. Its plate, its coaster and its words all go there.
 */
export function placeMoveOf(table: PlaceMoves, place: number): PlaceShift {
  const own = table.moves?.[place];
  return {
    along: mmWithin(own?.along, PLACE_MOVE_MAX_MM) + spacingAlong(place, table.spacing ?? 0),
    across: mmWithin(own?.across, PLACE_MOVE_MAX_MM),
  };
}

/**
 * The words' moves at every place for `StripSize.placeShift`: each tag's own move onto its plate
 * (`tags`, the mapping window's NAME TAGS) and the whole place moved for the evening (`table`). Along
 * the table, a place whose plate is linked (`plated`: those `placeAlong` reads) is already carried by
 * its plate, which moved with it, so only the rest is added there: the words never move twice.
 */
export function wordsShift(
  tags: Readonly<Partial<Record<number, PlaceShift>>> | null | undefined,
  table: PlaceMoves,
  plated: readonly number[] = [],
): Partial<Record<number, PlaceShift>> {
  const out: Partial<Record<number, PlaceShift>> = {};
  for (const place of ALL_PLACES) {
    const tag = tags?.[place];
    const move = placeMoveOf(table, place);
    const along = (tag?.along ?? 0) + (plated.includes(place) ? 0 : move.along);
    const across = (tag?.across ?? 0) + move.across;
    if (along !== 0 || across !== 0) out[place] = { along, across };
  }
  return out;
}

/**
 * A move of `shift` at `place`, as a direction on the strip in millimetres (x toward the TV end, y
 * across from the odd side's edge): along the table, and in toward the middle the way the guest looks
 * — straight across along the sides, on the 45° slant at the round end.
 */
export function shiftOnStrip(place: number, shift: PlaceShift): StripPoint {
  const odd = place % 2 === 1;
  if (Math.ceil(place / 2) > TABLE.rows) {
    return { x: shift.along + Math.SQRT1_2 * shift.across, y: (odd ? Math.SQRT1_2 : -Math.SQRT1_2) * shift.across };
  }
  return { x: shift.along, y: (odd ? 1 : -1) * shift.across };
}

/** Every place on the table, 1–12. */
export const ALL_PLACES: readonly number[] = Array.from({ length: TABLE_SEATS }, (_, i) => i + 1);

/** Each side's places, from the TV end out: its five along the side, then its seat at the round end. */
const ODD_SIDE = [1, 3, 5, 7, 9, 11] as const;
const EVEN_SIDE = [2, 4, 6, 8, 10, 12] as const;

/**
 * The ten places along the sides, each with a plate and coaster: an evening sells ten (Marek,
 * 2026-09-29). The round end's two are the backup for an eleventh and twelfth.
 */
export const SIDE_PLACES: readonly number[] = ALL_PLACES.filter((p) => p <= TABLE.rows * 2);

/**
 * The places the older free rows left open: the places along the sides past that many rows at the
 * bar end, per side. The two at the round end are not among them: they are the backup for an
 * eleventh and twelfth, so a guest added later takes a place along the sides, with its plate, first.
 */
export function placesFromFreeRows(freeRows = 0, freeRowsOdd: number | null = null): number[] {
  const even = Math.max(0, Math.floor(freeRows));
  const odd = freeRowsOdd === null || freeRowsOdd === undefined ? even : Math.max(0, Math.floor(freeRowsOdd));
  return SIDE_PLACES.filter((p) => Math.ceil(p / 2) > (p % 2 === 1 ? odd : even));
}

/** Each place once, in the order given; anything that is no place is dropped. */
function placesIn(list: readonly unknown[]): number[] {
  const out: number[] = [];
  for (const p of list) if (typeof p === 'number' && ALL_PLACES.includes(p) && !out.includes(p)) out.push(p);
  return out;
}

/**
 * The places laid on a strip, in the order the seats take them: `openPlaces`, else the free rows'
 * (laid by `layPlaces`), else all twelve.
 */
export function openPlacesOf(strip: Pick<StripSize, 'openPlaces' | 'freeRows' | 'freeRowsOdd'> = {}): number[] {
  if (Array.isArray(strip.openPlaces)) return placesIn(strip.openPlaces);
  if (strip.freeRows || (strip.freeRowsOdd !== null && strip.freeRowsOdd !== undefined)) {
    return layPlaces(placesFromFreeRows(strip.freeRows ?? 0, strip.freeRowsOdd ?? null));
  }
  return [...ALL_PLACES];
}

/** `places` taken in fill order from seat `first` on (see `seatOrder`). */
function fill(places: readonly number[], first: number): number[] {
  const odd = ODD_SIDE.filter((p) => places.includes(p));
  const even = EVEN_SIDE.filter((p) => places.includes(p));
  const out: number[] = [];
  let o = 0;
  let e = 0;
  while (out.length < odd.length + even.length) {
    const oddSeat = (first + out.length) % 2 === 1;
    if (oddSeat ? o < odd.length : e >= even.length) out.push(odd[o++]!);
    else out.push(even[e++]!);
  }
  return out;
}

/**
 * A set of places laid in the fill order (Marek, 2026-09-29), for `openPlaces`:
 *
 * 1. Each side's open places are taken from the TV end out — its five along the side, then its own
 *    seat at the round far end (11 is the odd side's, 12 the even side's).
 * 2. Odd seats take the odd side's, even seats the even side's, as always: seat 1 is the odd side's
 *    open place nearest the screen, seat 2 the even side's, seat 3 the odd side's next…
 * 3. When one side is full, the seats that are left carry on along the other side, in its order.
 *
 * With every place open this is seat n at place n.
 */
export function layPlaces(places: readonly number[]): number[] {
  return fill(placesIn(places), 1);
}

/**
 * Where each seat sits tonight: element n - 1 is seat n's place — the places laid, in their order,
 * then, for the seats past them (a crew larger than the places laid), the closed places by the same
 * rule as `layPlaces`. Every seat has a place of its own and no two share one.
 */
export function seatOrder(openPlaces: readonly number[] = ALL_PLACES): number[] {
  const laid = placesIn(openPlaces);
  const closed = ALL_PLACES.filter((p) => !laid.includes(p));
  return [...laid, ...fill(closed, laid.length + 1)];
}

/**
 * The places laid for the crew the room has now (Marek, 2026-09-29: a player added at the door is on
 * the table at once, and the whole table follows the number of players). A crew larger than the
 * places laid opens, for each new seat, the next place in the fill order (`seatOrder`: the place that
 * seat already takes) at the end of the list, so nobody already seated moves; those are `added`. A
 * crew that shrinks closes only places opened that way, the last first — never one the host opened
 * by hand.
 */
export function placesForCrew(
  openPlaces: readonly number[],
  added: readonly number[],
  crew: number,
): { openPlaces: number[]; added: number[] } {
  const want = Math.max(0, Math.min(TABLE_SEATS, Math.round(crew)));
  let laid = placesIn(openPlaces);
  let auto = placesIn(added).filter((p) => laid.includes(p));
  if (want > laid.length) {
    const more = seatOrder(laid).slice(laid.length, want);
    laid = [...laid, ...more];
    auto = [...auto, ...more];
  }
  while (laid.length > want && auto.includes(laid[laid.length - 1]!)) {
    const last = laid.pop()!;
    auto = auto.filter((p) => p !== last);
  }
  return { openPlaces: laid, added: auto };
}

/**
 * Rows left free at the bar end on one side of the table tonight: the closed places along that side
 * before its first open one (all five with none open).
 */
export function freeRowsOn(strip: Pick<StripSize, 'openPlaces' | 'freeRows' | 'freeRowsOdd'>, side: 'odd' | 'even'): number {
  const open = openPlacesOf(strip);
  const along = (side === 'odd' ? ODD_SIDE : EVEN_SIDE).slice(0, TABLE.rows);
  const first = along.findIndex((p) => open.includes(p));
  return first === -1 ? TABLE.rows : first;
}

/**
 * The seat at a place on the table tonight — the exact inverse of `placeOnTable`: the camera's
 * zones are the places, and this says whose they are. A closed place has one of the seats past the
 * open places (`seatOrder`), which is only laid when the crew is larger than the places open, so a
 * caller checks the seat is tonight's. Null for no place at all.
 *
 * The second argument may still be the older free rows (`freeRows`, then `freeRowsOdd`).
 */
export function seatAtPlace(
  place: number,
  strip: Pick<StripSize, 'openPlaces' | 'freeRows' | 'freeRowsOdd'> | number = {},
  freeRowsOdd: number | null = null,
): number | null {
  if (!Number.isInteger(place) || place < 1 || place > TABLE_SEATS) return null;
  const open = typeof strip === 'number' ? layPlaces(placesFromFreeRows(strip, freeRowsOdd)) : openPlacesOf(strip);
  return seatOrder(open).indexOf(place) + 1;
}

/**
 * The places laid after the host picks `wanted` from the phone's table map (Marek, 2026-09-30): the
 * places still wanted keep their order, so every seated guest keeps their place, and the new ones
 * follow in the fill order (`seatOrder`). The number of players is then how many are laid.
 */
export function relayPlaces(current: readonly number[], wanted: readonly number[]): number[] {
  const want = placesIn(wanted);
  const kept = placesIn(current).filter((p) => want.includes(p));
  const extra = seatOrder(kept).filter((p) => want.includes(p) && !kept.includes(p));
  return [...kept, ...extra];
}

/** The seated guests a change of places would move to another place: none may be. */
export function seatsMoved(current: readonly number[], next: readonly number[], seated: readonly number[]): number[] {
  const before = seatOrder(current);
  const after = seatOrder(next);
  return seated.filter((s) => s >= 1 && s <= TABLE_SEATS && (s > next.length || before[s - 1] !== after[s - 1]));
}

/** Places as letters for a short command: place 1 is `a`, 12 is `l`. */
export function placesToLetters(places: readonly number[]): string {
  return placesIn(places)
    .map((p) => String.fromCharCode(96 + p))
    .join('');
}

export function lettersToPlaces(letters: string): number[] {
  return placesIn([...letters].map((c) => c.charCodeAt(0) - 96));
}

/** Whether a place is laid tonight. */
export function isOpenPlace(place: number, strip: Pick<StripSize, 'openPlaces' | 'freeRows' | 'freeRowsOdd'> = {}): boolean {
  return openPlacesOf(strip).includes(place);
}

/**
 * How much of the strip's length is left to the round far end before the rows along the sides
 * begin. The two there sit close together near its tip; the ten along the sides are kept close
 * together from the TV end (Marek, 2026-09-25: "make spaces between all 10 smaller"), about 60 cm
 * each on the 345 × 80 cm strip, which leaves the round end room of its own.
 */
export function headLengthMm(strip: StripSize = TABLE): number {
  return strip.widthMm * HEAD_SHARE;
}

const HEAD_SHARE = 0.5625;

/** How far round the round end, from its tip, each of its two seats sits: close together near the tip. */
const HEAD_SPREAD_DEG = 30;

/**
 * The width each of the two at the round far end has for the words at their place: enough for a
 * call sign and a pour at the same size as along the sides.
 */
const HEAD_ROOM_SHARE = 0.52;

/**
 * Where a guest sits, seen from above. The room is laid out from the screen outward (seating.ts):
 * with every place open, seat 1 and 2 are the row nearest the TV, odd seats on the left of someone
 * facing the screen; with places closed (the mapping window), the seats fill the open ones in
 * `seatOrder`. The five rows share the strip's length past the round far end's tip evenly, whatever
 * it measures; places 11 and 12 sit round the far end, close together either side of its tip, their
 * words turned 45° to the table.
 */
export function placeOnTable(seat: number, strip: StripSize = TABLE): TablePlace {
  // Tonight's place for the seat: the places laid fill in `seatOrder`. A number that is no seat keeps
  // its own, as before.
  const place = Number.isInteger(seat) && seat >= 1 && seat <= TABLE_SEATS ? seatOrder(openPlacesOf(strip))[seat - 1]! : seat;
  const row = Math.min(TABLE.rows + 1, Math.max(1, Math.ceil(place / 2)));
  const odd = place % 2 === 1;
  const side = odd ? 'odd' : 'even';
  const inset = TABLE.placeInsetMm;
  // Moved by hand onto its plate (the mapping window's NAME TAGS), the guest's edge with it.
  const shift = placeShiftOf(strip, place);
  if (row > TABLE.rows) {
    // On the round end's half circle, HEAD_SPREAD_DEG either side of its tip; the words still turn
    // 45° toward each guest (Marek, 2026-09-25).
    const r = strip.widthMm / 2;
    const centre = { x: r, y: strip.widthMm / 2 };
    const spread = (HEAD_SPREAD_DEG * Math.PI) / 180;
    const out = { x: -Math.cos(spread), y: odd ? -Math.sin(spread) : Math.sin(spread) };
    const toward = { x: Math.SQRT1_2, y: odd ? Math.SQRT1_2 : -Math.SQRT1_2 };
    const edge = {
      x: centre.x + out.x * r + shift.along + toward.x * shift.across,
      y: centre.y + out.y * r + toward.y * shift.across,
    };
    return {
      seat,
      place,
      row,
      side,
      head: true,
      x: edge.x + toward.x * inset,
      y: edge.y + toward.y * inset,
      edge,
      toward,
      roomMm: strip.widthMm * HEAD_ROOM_SHARE,
    };
  }
  const start = headLengthMm(strip);
  const pitch = (strip.lengthMm - start) / TABLE.rows;
  // Where its plate is, when the table projector knows (`placeAlong`) — kept far enough from either
  // end that its words stay on the strip — else the even rows.
  const plate = strip.placeAlong?.[place];
  const x =
    (typeof plate === 'number' && Number.isFinite(plate)
      ? Math.min(strip.lengthMm - pitch / 2, Math.max(pitch / 2, plate))
      : strip.lengthMm - (row - 0.5) * pitch) + shift.along;
  const toward = { x: 0, y: odd ? 1 : -1 };
  const edge = { x, y: (odd ? 0 : strip.widthMm) + toward.y * shift.across };
  return { seat, place, row, side, head: false, x, y: edge.y + toward.y * inset, edge, toward, roomMm: pitch };
}

/** A point `mm` in from where a guest meets the strip, along the way they look. */
export function inFromPlace(place: Pick<TablePlace, 'edge' | 'toward'>, mm: number): StripPoint {
  return { x: place.edge.x + place.toward.x * mm, y: place.edge.y + place.toward.y * mm };
}

/**
 * The turn that makes words read the right way up from where a guest sits: canvas radians, 0 for
 * the even side (upright on the canvas), half a turn for the odd side, 45° either way off those for
 * the two at the round far end. `across` turns them to be read by whoever faces that guest.
 */
export function readingTurn(place: Pick<TablePlace, 'toward'>, across = false): number {
  const t = across ? { x: -place.toward.x, y: -place.toward.y } : place.toward;
  return Math.atan2(t.x, -t.y);
}

/**
 * The jump, as all three pictures play it, in milliseconds from the press that fired it.
 *
 * At the press the main projector's picture grows from the TV's rectangle to the whole wall, and
 * the jump film — made in that bigger format — fills it. The ship lifts off the far end of the
 * table and runs down the middle toward the wall; as it leaves the table the film shows the same
 * ship from behind, going into the wormhole. At the exit the picture goes back to the TV's
 * rectangle and the era film comes in — a soft fade, never a flash. The marks are measured off the
 * jump film as made (`print/MARKETING/film/show/jump.mp4`): recut the film, measure again.
 */
export const JUMP_RUN = {
  /**
   * The main projector's picture grows from the TV's rectangle to the whole wall at the press,
   * this fast: the jump film is made in the bigger format and fills all of it.
   */
  growMs: 1_200,
  /**
   * The ship reaches the TV end of the table and is gone — the moment its nose shows at the foot
   * of the wall in the film (2.75 s).
   */
  leaveTableMs: 2_750,
  /** The tunnel opens onto the era (the film's hole fills the wall by 14.75 s). */
  exitMs: 15_000,
  /** The era's own pictures are fully in. */
  settledMs: 16_500,
} as const;

/** Where the ship is on its run: 0 at the far end, 1 as it leaves the table. Slow off the mark, then fast. */
export function shipRun(elapsedMs: number): number {
  const t = Math.min(1, Math.max(0, elapsedMs / JUMP_RUN.leaveTableMs));
  return t * t * t;
}

/**
 * How much of the wall the main projector's picture fills: 0 is the TV's rectangle on the white
 * screen, 1 the whole projected wall. It grows at the press, stays for the tunnel, and goes back
 * to the TV's rectangle as the tunnel opens onto the era film, which is TV-sized.
 */
export function wallFill(elapsedMs: number | null): number {
  if (elapsedMs === null || elapsedMs < 0) return 0;
  const smooth = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
  if (elapsedMs < JUMP_RUN.exitMs) return smooth(elapsedMs / JUMP_RUN.growMs);
  return 1 - smooth((elapsedMs - JUMP_RUN.exitMs) / (JUMP_RUN.settledMs - JUMP_RUN.exitMs));
}

/**
 * The name tags (Marek, 2026-09-23): the table writes every laid seat's call sign at its place for
 * the whole evening, in place of the printed place cards, so everyone can read everyone. Only the
 * jumps take them away: gone at the press, back once the era's own pictures are in. Returns how
 * much of them shows, 0–1, for a jump `jumpMs` in (null when none is running).
 */
export function namesShown(jumpMs: number | null): number {
  if (jumpMs === null || jumpMs < 0) return 1;
  const smooth = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
  if (jumpMs < JUMP_RUN.exitMs) return 1 - smooth(jumpMs / NAMES_FADE_MS);
  return smooth((jumpMs - JUMP_RUN.settledMs) / NAMES_FADE_MS);
}

/** How long the name tags take to go at a jump and to come back after it. Never a snap. */
export const NAMES_FADE_MS = 800;

/** One entry of the menu as the table writes it. */
export interface TableLine {
  kind: 'pour' | 'plate';
  /** `POUR 2 OF 5 · 2213`, `PLATE 1 OF 3 · 2026`: which one, and the era it belongs to. */
  kicker: string;
  name: string;
  /** The bottle for a pour, what is on the plate for a plate. */
  line: string;
}

const pourLine = (ch: 1 | 2 | 3 | 4 | 5): TableLine => ({
  kind: 'pour',
  kicker: `POUR ${ch} OF 5 · ${SERVES[ch].era}`,
  name: SERVES[ch].name,
  line: SERVES[ch].ingredients,
});

const plateLine = (ch: 1 | 3 | 5): TableLine => ({
  kind: 'plate',
  kicker: `PLATE ${COURSES[ch].index} OF 3 · ${SERVES[ch].era}`,
  name: COURSES[ch].name,
  line: COURSES[ch].dish,
});

/**
 * The whole menu, in the order the room meets it: five pours and three plates, each plate after the
 * pour it follows. The table shows it down the middle at boarding, in place of the printed menu
 * card. Each entry names its era, as the printed card does (Marek, 2026-09-25: "name the eras").
 */
export function tableMenu(): TableLine[] {
  return [pourLine(1), plateLine(1), pourLine(2), pourLine(3), plateLine(3), pourLine(4), pourLine(5), plateLine(5)];
}

/** What is being served right now, for every place on the table: the pour while it is poured, the plate while it lands. */
export function servedNow(chapter: Chapter, phase: string): TableLine | null {
  if (chapter === 0) return null;
  if (phase === 'serve') return pourLine(chapter);
  if (phase === 'course' && (chapter === 1 || chapter === 3 || chapter === 5)) return plateLine(chapter);
  return null;
}
